import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import { buyOffer, priceLabel, type Offer } from "@/lib/monetization";
import { track } from "@/lib/track";

// One paid module, as a person sees it: what it is, its price, one button.
// Renders nothing unless the module is on for this person (and shows a quiet
// "Active" line when they already have it). Copy comes from the catalog.
interface Props {
  offer: Offer | undefined;
  /** Where Stripe sends them back after paying. */
  returnPath?: string;
  /** Extra line under the description (e.g. "for Casa Luna"). */
  context?: string;
  /** Button text; defaults by module kind. */
  cta?: string;
  /** For listing modules on /pricing: there is no listing to buy for yet. */
  chooseListingHref?: string;
  compact?: boolean;
}

// Buttons say what happens (checklist: labels start with a verb and the CTA
// explains the next step), never a bare "Buy".
const defaultCta = (o: Offer) =>
  o.module.startsWith("verified_") ? (o.amountCents === 0 ? "Verify my identity" : "Get verified")
    : o.module === "featured_listing" ? "Feature my home"
    : o.module === "bump_72h" ? "Bump to the top"
    : o.kind === "subscription" ? "Subscribe" : "Buy";

export default function OfferCard({ offer, returnPath, context, cta, chooseListingHref, compact = false }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  if (!offer || (offer.state !== "available" && offer.state !== "active")) return null;

  const pay = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    track("offer_clicked", { module: offer.module, amount_cents: offer.amountCents });
    setBusy(true);
    try {
      const r = await buyOffer(offer, { returnPath, cancelPath: location.pathname + location.search });
      if (r.url) {
        track("checkout_started", { module: offer.module, amount_cents: offer.amountCents });
        window.location.assign(r.url);
      }
      else if (r.next) navigate(r.next);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Payment could not start.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-card border bg-card ${compact ? "p-3" : "p-4"} flex flex-col sm:flex-row sm:items-center gap-3`} data-testid={`offer-${offer.module}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{offer.name}</p>
        {!compact && offer.description && <p className="text-xs text-muted-foreground mt-0.5">{offer.description}</p>}
        {context && <p className="text-xs text-muted-foreground mt-0.5">{context}</p>}
        {offer.testMode && offer.state === "available" && (
          <p className="text-xs text-amber-700 mt-0.5" data-testid="offer-test-mode">Test mode: pay with 4242 4242 4242 4242, any future date, any CVC.</p>
        )}
        {!compact && offer.state === "available" && (
          <p className="text-xs text-muted-foreground mt-1.5" data-testid="offer-trust">
            Secure payment by Stripe. <a href="/refunds" className="underline underline-offset-2">Refund policy</a>.
          </p>
        )}
      </div>
      {offer.state === "active" ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-jungle" data-testid="offer-active">
          <Check className="h-3.5 w-3.5" /> Active{offer.endsAt ? ` until ${format(new Date(offer.endsAt), "MMM d")}` : ""}
        </span>
      ) : (
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm font-semibold">{priceLabel(offer)}</span>
          {chooseListingHref ? (
            <Button asChild size="sm" variant="outline" className="rounded-full"><a href={chooseListingHref}>Choose a listing</a></Button>
          ) : (
            <Button size="sm" className="rounded-full" onClick={pay} disabled={busy}>
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              {cta ?? defaultCta(offer)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
